import type { MembershipRole, MembershipSubjectType, NodeUser, Profile } from 'models';
import type { User } from 'features/auth';

// TODO: rename
const hasMembershipInProfile = ({
  role,
  subjectId,
  subjectType,
  viewer,
}: {
  role?: MembershipRole;
  subjectId?: string;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}) =>
  viewer.memberships.some(
    (membership) =>
      membership.subjectType === subjectType &&
      (!role || membership.role === role) &&
      (!subjectId || membership.subjectId === subjectId),
  );

const hasRoleInSubjectProfile = ({
  role,
  subjectType,
  viewer,
}: {
  role: MembershipRole;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}): boolean => hasMembershipInProfile({ role, subjectType, viewer });

const hasRoleInSomeSubjectProfile = ({
  role,
  subjectId,
  subjectType,
  viewer,
}: {
  role: MembershipRole;
  subjectId: string;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}): boolean => hasMembershipInProfile({ role, subjectId, subjectType, viewer });

const hasMembershipInSubjectProfile = ({
  subjectId,
  subjectType,
  viewer,
}: {
  subjectId: string;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}): boolean => hasMembershipInProfile({ subjectId, subjectType, viewer });

export const hasMembershipInCommunityProfile = ({ communityId, viewer }: { communityId: string; viewer: Profile }) =>
  hasMembershipInSubjectProfile({ subjectId: communityId, subjectType: 'Community', viewer });

export const hasMembershipInCommunity = ({ communityId, viewer }: { communityId: string; viewer: User }) =>
  hasMembershipInSubjectProfile({ subjectId: communityId, subjectType: 'Community', viewer: viewer.railsUserData });

export const hasMembershipInOrganizationProfile = ({
  organizationId,
  viewer,
}: {
  organizationId: string;
  viewer: Profile;
}) => hasMembershipInSubjectProfile({ subjectId: organizationId, subjectType: 'Organization', viewer });

export const isGroupsAdmin = ({ viewer }: { viewer: Profile }) =>
  hasRoleInSubjectProfile({ role: 'admin', subjectType: 'Group', viewer });

export const isMedicalGroupAdmin = ({ nodeViewer }: { nodeViewer: NodeUser }) =>
  Boolean(
    nodeViewer.memberships?.some(
      (membership) => membership.role === 'admin' && membership.type === 'group.medicalGroup',
    ),
  );

export const isEngagementSpecialist = ({ viewer }: { viewer: Profile }) =>
  hasRoleInSubjectProfile({ role: 'navigator', subjectType: 'Organization', viewer });

export const isGroupAdmin = ({ groupId, viewer }: { groupId: string; viewer: Profile }) =>
  hasRoleInSomeSubjectProfile({ role: 'admin', subjectId: groupId, subjectType: 'Group', viewer });

export const isProgramAdminProfile = ({ programId, viewer }: { programId: string; viewer: Profile }) =>
  hasRoleInSomeSubjectProfile({ role: 'admin', subjectId: programId, subjectType: 'Program', viewer });

export const isProgramAuthorProfile = ({ programId, viewer }: { programId: string; viewer: Profile }) =>
  viewer.authorPrograms.includes(programId);

export const hasMembershipInMultipleCommunitiesProfile = ({ viewer }: { viewer: Profile }) =>
  (viewer.membershipsCount.joinedCommunities ?? 0) > 1;

export const hasMembershipInProgramProfile = ({ programId, viewer }: { programId: string; viewer: Profile }) =>
  hasMembershipInSubjectProfile({ subjectId: programId, subjectType: 'Program', viewer });

export const isMaintainer = ({ viewer }: { viewer: Profile }) => viewer.maintenance;
